import React, { useContext, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import uuid from 'react-uuid';
import { ReactComponent as DominoMask } from '../../assets/images/domino-mask.svg';
import Loader from '../../components/common/loader';
import Button from '../../components/Elements/button/button';
import PageHeader from '../../components/page-header';
import { OrganisationContext } from '../../context/organisationContext';
import { getItemFromLocalStorage } from '../../helpers/localstorage';
import { applyMasqueradeUser, buildExitMasqueradeUser, buildMasqueradeUser } from '../../helpers/masquerade';
import useDebounce from '../../helpers/useDebounceHook';
import { getAccountList, onAccessAccount, onAccessAccountUsingOrignalToken } from '../../store/features/groupsSlice';
import { addToast } from '../../store/features/toastSlice';
import { setUser } from '../../store/features/userSlice';
import { AccountsWrapper } from './accounts.styled';

const PAGE_SIZE = 20;

/**
 * Accounts in the signed-in organisation's network, and the entry point for masquerading into one.
 * Mirrors the dashboard's accounts list (components/accounts-list) - the exit and account-switch
 * halves already live in components/sidebar/sidebar.js.
 */
const Accounts = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { hasPermission } = useContext(OrganisationContext);
  const { user } = useSelector(state => state.user);
  const { accountList } = useSelector(state => state.groups);

  const [search, setSearchText] = useState('');
  const [selectedPage, setSelectedPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [accessingId, setAccessingId] = useState(null);
  const debouncedSearch = useDebounce(search, 500);

  const canMasquerade = hasPermission('MASQUERADE');
  const isMasquerading = Boolean(user?.isAccessAccount);

  // While masquerading, the network belongs to the original session, not the account we are inside.
  const organizationID = isMasquerading
    ? user?.original_organizations?.[0]?.id
    : getItemFromLocalStorage('user')?.organizations?.[0]?.id;

  const fetchAccounts = currentPage => {
    if (!organizationID) return;
    setLoading(true);
    dispatch(
      getAccountList({
        organizationID,
        params: { page: currentPage, size: PAGE_SIZE, search: debouncedSearch },
      }),
    )
      .then(() => setLoading(false))
      .catch(() => {
        setLoading(false);
        dispatch(addToast({ error: true, text: 'Error while fetching accounts', id: uuid() }));
      });
  };

  useEffect(() => {
    setSelectedPage(0);
  }, [debouncedSearch]);

  useEffect(() => {
    fetchAccounts(selectedPage);
  }, [organizationID, debouncedSearch, selectedPage]);

  const accounts = accountList?.content || [];

  const onAccessAccountClick = account => {
    setAccessingId(account.id);
    // An already masqueraded token cannot masquerade again, so switching accounts has to go out
    // over the original token.
    const request = isMasquerading
      ? onAccessAccountUsingOrignalToken({ organizationId: account.id })
      : onAccessAccount({ organizationId: account.id });

    dispatch(request)
      .then(data => {
        applyMasqueradeUser(dispatch, setUser, buildMasqueradeUser(user, account, data, accounts));
        setAccessingId(null);
        dispatch(addToast({ error: false, text: `Now accessing ${account.name}`, id: uuid() }));
        navigate('/forms', { replace: true });
      })
      .catch(() => {
        setAccessingId(null);
        dispatch(addToast({ error: true, text: 'Account cannot be accessed', id: uuid() }));
      });
  };

  const onExitMasquerade = () => {
    applyMasqueradeUser(dispatch, setUser, buildExitMasqueradeUser(user));
    dispatch(addToast({ error: false, text: 'Exited masquerade', id: uuid() }));
    navigate('/forms', { replace: true });
  };

  return (
    <AccountsWrapper className="flex-column flex-1">
      <PageHeader
        names={[{ text: 'Accounts', onClick: () => {} }]}
        showSearch
        showPagination
        searchText={search}
        onSearchChange={setSearchText}
        pagination={{
          selectedPage: selectedPage,
          totalPages: accountList?.total_pages || 0,
          setSelectedPage: setSelectedPage,
        }}
      />

      {isMasquerading && (
        <div className="masquerading-banner flex items-center justify-between mx-6 mt-4 pxy-3">
          <div className="flex items-center">
            <DominoMask height={16} width={16} />
            <label className="regular-text ml-2">
              {`Currently accessing ${
                user?.selectedAccount?.name || 'an account'
              } — picking another switches straight to it.`}
            </label>
          </div>
          <Button className="default-secondary" size="small" label="Exit masquerade" onClick={onExitMasquerade} />
        </div>
      )}

      {!canMasquerade && (
        <label className="no-permission regular-text mx-6 mt-4">
          You do not have the MASQUERADE permission, so accounts are read-only here.
        </label>
      )}

      <div className="flex-column flex-1 mt-6 mx-6 overflow-auto">
        <div className="list-header pxy-4">
          <label className="medium-text">Name</label>
          <label className="medium-text">Location</label>
          <label className="medium-text">Category</label>
          <label className="medium-text">{canMasquerade ? 'Access' : ''}</label>
        </div>

        {loading ? (
          <Loader />
        ) : accounts.length === 0 ? (
          <label className="muted regular-text mt-10 flex justify-center">No accounts found</label>
        ) : (
          accounts.map(account => {
            const isCurrent = isMasquerading && user?.selectedAccount?.id === account.id;
            return (
              <div key={account.id} className={`list-row pxy-4 ${isCurrent ? 'current-account' : ''}`}>
                <div className="flex items-center">
                  {isCurrent && <DominoMask height={14} width={14} className="mr-2" />}
                  <label className="regular-text">{account.name}</label>
                </div>
                <label className="muted regular-text">{account.primary_location?.formatted_address || '—'}</label>
                <label className="muted regular-text">{account.category?.name || '—'}</label>
                <div className="flex items-center">
                  {canMasquerade && !isCurrent && (
                    <Button
                      className={accessingId === account.id ? 'primary disabled' : 'primary'}
                      size="small"
                      label={accessingId === account.id ? 'Accessing…' : 'Access account'}
                      onClick={() => onAccessAccountClick(account)}
                    />
                  )}
                  {isCurrent && <label className="muted regular-text">Current</label>}
                </div>
              </div>
            );
          })
        )}
      </div>
    </AccountsWrapper>
  );
};

export default Accounts;
